import { useEffect, useMemo, useState } from 'react';
import { customerPickerTitle } from '../lib/jobPickers';
import { Select, TextInput } from './ui/Field';
import type { Customer } from '../types/models';

const OTHER_VALUE = '__other__';

interface Props {
  customers: Customer[];
  value: string;
  onChange: (value: string, customer?: Customer) => void;
  className?: string;
  id?: string;
  /** Required before completion and not yet supplied — see Field. */
  outstanding?: boolean;
}

export function CustomerPicker({ customers, value, onChange, className, id, outstanding }: Props) {
  const [usingOther, setUsingOther] = useState(false);
  const customerNames = useMemo(
    () =>
      customers
        .map((customer) => customer.name?.trim())
        .filter((name): name is string => !!name),
    [customers]
  );
  const knownCustomer = customers.find((customer) => customer.name === value);
  const hasKnownName = !!value && customerNames.includes(value);
  const selectedValue = usingOther
    ? OTHER_VALUE
    : value && !hasKnownName
      ? value
      : value || '';

  useEffect(() => {
    if (hasKnownName || !value) setUsingOther(false);
  }, [hasKnownName, value]);

  return (
    <div className={className}>
      <Select
        id={id}
        outstanding={outstanding}
        value={selectedValue}
        onChange={(event) => {
          const nextValue = event.target.value;
          if (nextValue === OTHER_VALUE) {
            setUsingOther(true);
            onChange('');
            return;
          }

          setUsingOther(false);
          const customer = customers.find((record) => record.name === nextValue);
          onChange(nextValue, customer);
        }}
      >
        <option value="">Select customer</option>
        {customers.map((customer) => {
          if (!customer.name) return null;
          return (
            <option key={customer.id ?? customer.name} value={customer.name}>
              {customerPickerTitle(customer)}
            </option>
          );
        })}
        {value && !hasKnownName && !usingOther && (
          <option value={value}>{value}</option>
        )}
        <option value={OTHER_VALUE}>Other</option>
      </Select>

      {(usingOther || (!knownCustomer && value && !hasKnownName)) && (
        <TextInput
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder="Enter customer name"
          aria-label="Customer name"
          outstanding={outstanding}
          className="mt-2"
        />
      )}
    </div>
  );
}
